<script>
import AjaxLoad from "@/components/commonComponents/AjaxLoad.vue";
import BitrixImage from "@/components/commonComponents/BitrixImage.vue";
import ResponsiveImage from "@/components/commonComponents/ResponsiveImage.vue";
import VFileInputUpload from "@/components/commonComponents/VFileInputUpload.vue";

export default {
    name: "QuotesModalPhoto",
    components: {
        VFileInputUpload,
        BitrixImage,
        ResponsiveImage,
        AjaxLoad,
    },
    props: {
        value: {
            type: Object,
            default: () => ({}),
        },
        idProject: {
            type: String,
        },
        isDisabledField: {
            type: Function,
        },
    },
    data() {
        return {
            isLoadingImage: true,
            isError: false,
            resizeMode: false,
            photoSize: {
                width: 151,
                height: 231,
                paddings: 16,
            },
            internalCropData: {
                x: undefined,
                y: undefined,
                scale: undefined,
                w: undefined,
                h: undefined,
            },
        };
    },
    computed: {
        uploadUrl() {
            return this.$apiUrl.getQuoteSpeakerUploadUrl({
                id_project: this.idProject,
                id_speaker: this.value.id_speaker,
            });
        },
        photoRatio() {
            return this.photoSize.width / this.photoSize.height;
        },
        photoContainerRatio() {
            return (this.photoSize.width + this.photoSize.paddings * 2) / (this.photoSize.height + this.photoSize.paddings * 2);
        },
        xCrop: {
            get() {
                return this.value?.photo_crop === null
                        ? this.internalCropData.x
                        : this._.isFinite(this.value?.photo_crop?.x)
                            ? this.value.photo_crop.x
                            : undefined;
            },
            set(v) {
                this.setCropDataField('x', v);
            }
        },
        yCrop: {
            get() {
                return this.value?.photo_crop === null
                        ? this.internalCropData.y
                        : this._.isFinite(this.value?.photo_crop?.y)
                            ? this.value.photo_crop.y
                            : undefined;
            },
            set(v) {
                this.setCropDataField('y', v);
            }
        },
        scaleCrop: {
            get() {
                return this.value?.photo_crop === null && this.internalCropData.scale 
                        || this.value?.photo_crop?.scale 
                        || undefined;
            },
            set(v) {
                this.setCropDataField('scale', v);
            }
        },
        cardTextStyle() {
            return {
                aspectRatio: this.value?.photo ? this.photoContainerRatio : null, 
                lineHeight: 0
            };
        },
    },
    watch: {
        'value.photo': {
            handler() {
                this.isError = false;
            },
        },
        'value.id_speaker': {
            handler() {
                this.isError = false;
            },
        },
    },
    created() {
        this.internalCropData.w = this.photoSize.width;
        this.internalCropData.h = this.photoSize.height;
    },
    methods: {
        getSrc(file) {
            return [process.env.VUE_APP_BACKEND_URL, file].join('');
        },
        setCropDataField(field, v) {
            if(!this.value) {
                return;
            }
            if (!this.resizeMode) {
                this.internalCropData[field] = v;
                return;
            }
            if(!this.value.photo_crop?.w) {
                const data = this._(this.value.photo_crop)
                                    .merge(this.internalCropData)
                                    .mapValues((v, k) => v === undefined ? k === 'scale' && 1 || 0 : v)
                                    .value();
                this.$set(this.value, 'photo_crop', data);
            }
            this.$set(this.value.photo_crop, field, v);
            this.$emit('input', this.value);
        },
        onLoadStart() {
            this.isError = null;
            this.isLoadingImage = true;
        },
        onLoadFinish() {
            this.isLoadingImage = false;
        },
        onLoadError() {
            this.isLoadingImage = false;
            this.isError = true;
        },
        onClearInputUpload() {
            this.value.photo_crop = null; 
            this.value.photo = null;
        },
    },
}
</script>

<template>
    <v-container>
        <v-row>
            <v-col class="photo-wrapper">
                <v-card :max-width="photoSize.width + photoSize.paddings * 2">
                    <v-card-text :style="cardTextStyle" :class="{'pa-4': true, 'card-image-loading':isLoadingImage}">
                        <template v-if="value?.id_speaker">
                            <v-skeleton-loader
                                v-if="isLoadingImage"
                                type="image"
                                width="100%"
                                height="100%"
                            ></v-skeleton-loader>
                            <v-img
                                v-else-if="isError"
                                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mM88R8AApUByU2MEcEAAAAASUVORK5CYII="
                                fill
                                max-width="100%"
                                max-height="100%"
                                :aspect-ratio="photoRatio"
                            ></v-img>
                            
                            <template v-if="!isError">
                                <responsive-image
                                    v-if="value?.photo"
                                    :src="getSrc(value.photo)"
                                    @loadstart="onLoadStart"
                                    @load="onLoadFinish"
                                    @error="onLoadError"
                                    crop-image
                                    :draggable="resizeMode"
                                    :x.sync="xCrop"
                                    :y.sync="yCrop"
                                    :scale.sync="scaleCrop"
                                ></responsive-image>
                                <ajax-load
                                    v-else
                                    :url="$apiUrl.getSpeakerUrl({id_speaker: value.id_speaker})"
                                    :transform="response => response.data.data.items[0]"
                                    v-slot="{data}"
                                    @loading="onLoadStart"
                                >
                                    <bitrix-image
                                        :file="data?.PREVIEW_PICTURE_FILE"
                                        @loadstart="onLoadStart"
                                        @load="onLoadFinish"
                                        @error="onLoadError"
                                        max-width="151"
                                        max-height="231"
                                        contain
                                    ></bitrix-image>
                                </ajax-load>

                                <v-btn
                                    v-if="!isLoadingImage && !isError && value?.photo && !isDisabledField('photo')"
                                    class="resize-button"
                                    :class="{'resize-button_hide': resizeMode}"
                                    icon
                                    color="white"
                                    @click="resizeMode = true"
                                >
                                    <!--
                                    @TODO На потом, возможно, mdi-crop, mdi-crop-free
                                    -->
                                    <v-icon>mdi-resize-bottom-right</v-icon>
                                </v-btn>
                            </template>
                        </template>
                    </v-card-text>

                    <v-card-actions class="px-4">
                        <v-file-input-upload
                            v-model="value.photo"
                            :upload-url="uploadUrl"
                            :disabled="isDisabledField('photo')"
                            accept="image/png, image/jpeg, image/webp"
                            clearable
                            label="Фото"
                            prepend-icon="mdi-camera"
                            :truncate-length="12"
                            @input="value.photo_crop = null"
                            @clear="onClearInputUpload"
                        ></v-file-input-upload>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<style scoped lang="scss">
    ::v-deep {
        .v-card__actions {
            border-top: 1px solid silver;
        }

        .photo-wrapper {
            background-color: #00000016;
            //box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12);
            //margin: 2px;
        }
        .card-image-loading {
            height: 233px;
        }
        .v-card {
            margin: 0 auto;
            .v-card__text {
                position: relative;
            }
        }
        .v-skeleton-loader {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 999;
        }
        .v-skeleton-loader__bone {
            width: 100%;
            height: 100%;
        }
    }
    .resize-button {
        position: absolute;
        bottom: 16px;
        right: 16px;
        background-color: rgba(0, 0, 0, 0.3);
        box-shadow: 0px -1px -2px rgba(0, 0, 0, 0.1);
        /**
        Я бы предпочел что-то вроде mix-blend-mode: difference;, возможно, в улучшенном варианте. Т.к. каждый конкретный фон вполне может быть белым...
        -----
        тут же подложка есть темная rgba(0, 0, 0, 0.3) на белом фоне видно,
        без подложки при difference, на фотках, где разные мелкие цвета тоже почти не видна иконка
         */
        color: white !important;
        height: 36px;
        width: 36px;
        clip-path: polygon(100% 0, 100% 100%, 0% 100%);
        transition: all 0.3s;
        border-radius: 0;
        overflow: hidden;
        &:hover {
            box-shadow: 0px -2px -5px rgba(0, 0, 0, 0.3);
            background-color: rgba(0, 0, 0, 0.5);
        }
        &_hide {
            width: 0;
        }
    }
</style>